// 取得済みの注文明細に検索条件を適用する(純関数)。
//
// Firestore からの取得は「日付範囲」だけで行い、状態・部署・「このアカウントのみ」は
// ここで絞る。これにより
//  ・状態/部署/myOwn の切り替えは Firestore の再読なし(メモ化フィルタのみ)で即時反映
//  ・複合インデックスが不要(範囲条件は単一フィールドのみ)
//  ・Firestore の in × in の論理和上限(30)を踏まない(部署は20超 × 状態4 で超過し得る)
// ※ ルール上、認証済みユーザーは orders を全件読めるため、クライアント側で絞ることに
//   セキュリティ上の後退はない(現行も myOwn はクライアント側で絞っていた)。
import { toDateOrNull } from "./orderDateUpdate";
import {
  DATE_KIND_OPTIONS,
  STATUS_IDS,
  dateKindToField,
  statusLabelsForIds,
} from "./orderSearchModel";

const dayStartMillis = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
const dayEndMillis = (date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  ).getTime();

// 条件から判定関数を作る(Set 構築を1回で済ませるため)。
// 日付種類は OR: 選択したいずれかの日付が期間内なら対象。
export const buildOrderMatcher = (conditions, loginUserEmail) => {
  const fromMs = dayStartMillis(conditions.from);
  const toMs = dayEndMillis(conditions.to);
  const dateFields = conditions.dateKinds.map(dateKindToField);
  // 全種選択(=すべて)のときは状態で絞らない。旧実装の「すべて=状態条件なし」と同じで、
  // 想定外の status 値や欠落を持つ古い doc も「すべて」でなら見えるようにする
  // (集合一致必須にすると、そうした doc はどの選択でも表示されなくなる)
  const statusSet =
    conditions.statuses.length === STATUS_IDS.length
      ? null
      : new Set(statusLabelsForIds(conditions.statuses));
  // 部署IDは文字列に寄せて比較する(マスタ・注文docで型が揺れても一致させる)
  const departmentSet =
    conditions.departmentIds === null
      ? null
      : new Set(conditions.departmentIds.map(String));

  return (order) => {
    if (!order) return false;
    if (statusSet && !statusSet.has(order.status)) return false;
    if (departmentSet && !departmentSet.has(String(order.order_department_id))) {
      return false;
    }
    if (conditions.myOwn && order.login_user_email !== loginUserEmail) {
      return false;
    }
    return dateFields.some((field) => {
      const date = toDateOrNull(order[field]);
      if (!date) return false;
      const ms = date.getTime();
      return fromMs <= ms && ms <= toMs;
    });
  };
};

// ソートに使うフィールド: 選択中の日付種類のうち業務順(注文→発注→検収→取消)で
// 最初のものの降順。「すべて」選択時は注文日になる
// (旧実装の「状態=すべてなら updated_at」は編集のたびに並びが変わり予測しづらかった)。
export const getSortField = (conditions) =>
  DATE_KIND_OPTIONS.find((option) => conditions.dateKinds.includes(option.id))
    ?.field ?? "placed_at";

// 降順ソート。値なしは末尾、同時刻は id で安定化
// (リアルタイム更新のスナップショットごとに並びが揺れないようにする)。
export const sortOrders = (orders, conditions) => {
  const field = getSortField(conditions);
  return [...orders].sort((a, b) => {
    const aMs = toDateOrNull(a?.[field])?.getTime() ?? null;
    const bMs = toDateOrNull(b?.[field])?.getTime() ?? null;
    if (aMs === null && bMs === null) {
      return String(a?.id).localeCompare(String(b?.id));
    }
    if (aMs === null) return 1;
    if (bMs === null) return -1;
    if (bMs !== aMs) return bMs - aMs;
    return String(a?.id).localeCompare(String(b?.id));
  });
};

export const applyOrderSearch = (orders, conditions, loginUserEmail) => {
  const matches = buildOrderMatcher(conditions, loginUserEmail);
  return sortOrders((orders || []).filter(matches), conditions);
};
